import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './QuizPage.css';
import API_BASE_URL from '../config';

const QuizPage = () => {
    const { examType, testId } = useParams();
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [userAnswers, setUserAnswers] = useState({});
    const [passageContent, setPassageContent] = useState('');
    const [audioUrl, setAudioUrl] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const questionRefs = useRef({});
    const [zoomLevel, setZoomLevel] = useState(100);
    const [resultData, setResultData] = useState(null);

    // FIX: normalize examType để tránh mismatch UI
    const normalizedExamType = examType?.toLowerCase();

    // FIX: safe URL handler (QUAN TRỌNG)
    const safeUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        return `${API_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;
    };

    const getInitialTime = () => {
        if (normalizedExamType === 'reading') return 60 * 60;
        if (normalizedExamType === 'listening') return 30 * 60;
        if (normalizedExamType === 'writing') return 60 * 60;
        return 60 * 60;
    };

    const [timeLeft, setTimeLeft] = useState(getInitialTime());

    const handleSubmit = useCallback(async (isAutoSubmit = false) => {
        let scoreCount = 0;
        if (questions.length === 0) return;

        questions.forEach((q) => {
            const studentAns = userAnswers[q.question_number]?.trim().toLowerCase();
            const correctAns = q.correct_answer.toLowerCase();
            if (!studentAns) return;

            if (correctAns.includes('/')) {
                const parts = correctAns.split('/');
                if (parts.some(p => p.trim() === studentAns)) scoreCount++;
            } else if (studentAns === correctAns) {
                scoreCount++;
            }
        });

        const finalScore = (scoreCount / questions.length) * 9;
        const user = JSON.parse(localStorage.getItem('user'));

        try {
            if (user) {
                await axios.post(`${API_BASE_URL}/api/results`, {
                    user_id: user.id,
                    exam_type: `${examType} - ${testId}`,
                    score: finalScore.toFixed(1),
                    correct_answers: scoreCount
                });
            }

            setResultData({
                    score: finalScore.toFixed(1),
                    correct: scoreCount,
                    total: questions.length,
                    auto: isAutoSubmit
            });
        } catch (err) {
            alert("Error saving results!");
        }
    }, [questions, userAnswers, examType, testId, navigate]);

    useEffect(() => {
        if (timeLeft <= 0) {
            handleSubmit(true);
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft(prev => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft, handleSubmit]);

    useEffect(() => {
        const fetchExamData = async () => {
            try {
                console.log("Đang gọi API cho testId:", testId);

                const examRes = await axios.get(`${API_BASE_URL}/api/exams/detail/${testId}`);
                const examData = Array.isArray(examRes.data) ? examRes.data[0] : examRes.data;

                if (examData) {
                    setPassageContent(examData.content);

                    if (examData.audio_url) {
                        setAudioUrl(examData.audio_url);
                    }

                    if (examData.image_url) {
                        setImageUrl(examData.image_url);
                    }
                }

                const quesRes = await axios.get(`${API_BASE_URL}/api/questions`, {
                    params: { type: examType, testId: testId }
                });

                if (quesRes.data && quesRes.data.length > 0) {
                    setQuestions(quesRes.data);
                }

            } catch (error) {
                console.error("LỖI KẾT NỐI API:", error);
            }
        };

        if (testId) fetchExamData();
    }, [examType, testId]);

    const scrollToQuestion = (qNum) => {
        const element = questionRefs.current[qNum];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('active-focus');
            setTimeout(() => element.classList.remove('active-focus'), 1500);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const handleInputChange = (qNum, value) => {
        setUserAnswers({ ...userAnswers, [qNum]: value });
    };

    const handleHighlight = () => {
        const selection = window.getSelection();

        if (selection.rangeCount > 0 && selection.toString().trim().length > 0) {
            const range = selection.getRangeAt(0);
            const mark = document.createElement('mark');
            mark.style.backgroundColor = '#ffeb3b';
            mark.style.color = 'black';

            try {
                range.surroundContents(mark);
            } catch (e) {
                alert("Vui lòng chỉ highlight văn bản trong cùng một đoạn.");
            }

            selection.removeAllRanges();
        }
    };

    return (
        <div className="ielts-quiz-container">
            <div className="ielts-black-header">
                <div className="candidate-info">
                    <span className="icon-user">👤</span>{' '}
                    {JSON.parse(localStorage.getItem('user'))?.name || 'Candidate'} - 123456
                </div>

                <div className="timer-display">
                    <span className="clock-icon">⏰</span>{' '}
                    <strong>{formatTime(timeLeft)}</strong> minutes left
                </div>

                <div className="header-buttons">
                    <button className="btn-help">Help ❓</button>
                    <button className="btn-hide" onClick={() => navigate('/dashboard')}>
                        Hide 👁
                    </button>
                </div>
            </div>

            <div className="quiz-main-layout">

                <div className="content-side ielts-passage-view">

                    {/* AUDIO FIXED */}
                    {normalizedExamType === 'listening' && audioUrl && (
                        <div style={{
                            marginBottom: '30px',
                            padding: '20px',
                            background: '#f8f9fa',
                            borderRadius: '12px',
                            border: '1px solid #dee2e6',
                            position: 'sticky',
                            top: '0',
                            zIndex: '10'
                        }}>
                            <p style={{ marginTop: 0 }}><b>Audio Track:</b></p>

                            <audio controls key={audioUrl} style={{ width: '100%' }}>
                                <source src={safeUrl(audioUrl)} type="audio/mpeg" />
                            </audio>
                        </div>
                    )}

                    <div className="passage-toolbar" style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '15px',
                        paddingBottom: '10px',
                        borderBottom: '1px solid #eee',
                        position: 'sticky',
                        top: '0',
                        background: '#fff',
                        zIndex: 9
                    }}>
                        <button onClick={() => setZoomLevel(z => Math.min(z + 10, 200))}>➕ Zoom In</button>
                        <button onClick={() => setZoomLevel(z => Math.max(z - 10, 80))}>➖ Zoom Out</button>
                        <button onClick={handleHighlight} style={{ backgroundColor: '#ffeb3b', border: '1px solid #ccc' }}>
                            🖍 Highlight
                        </button>
                        <span style={{ fontSize: '12px', color: '#666' }}>Zoom: {zoomLevel}%</span>
                    </div>

                    {/* IMAGE FIXED */}
                    {imageUrl && (
                        <div style={{ marginTop: '20px' }}>
                            <img
                                src={safeUrl(imageUrl)}
                                alt="Exam"
                                style={{ width: '100%', borderRadius: '8px' }}
                            />
                        </div>
                    )}

                    <div
                        className="passage-content"
                        style={{ fontSize: `${zoomLevel}%`, transition: 'font-size 0.2s ease-in-out' }}
                        dangerouslySetInnerHTML={{ __html: passageContent }}
                    />
                </div>

                <div className="question-side">
                    <div className="question-header">
                        <h2>{examType.toUpperCase()} - TEST</h2>
                    </div>

                    <div className="questions-container">
                        {questions.map((q) => (
                            <div
                                key={q.question_number}
                                className="question-block-item"
                                ref={el => questionRefs.current[q.question_number] = el}
                            >
                                <p className="question-text">
                                    <strong>{q.question_number}.</strong> {q.question_text}
                                </p>

                                <div className="answer-input-area">
                                    {q.question_type === 'mcq_one' ? (
                                        <div className="options-grid">
                                            {q.options && JSON.parse(q.options)
                                                .filter(opt => opt.trim() !== '')
                                                .map((opt, index) => (
                                                    <label key={index} className="option-label">
                                                        <input
                                                            type="radio"
                                                            name={`q-${q.question_number}`}
                                                            checked={userAnswers[q.question_number] === opt}
                                                            onChange={() => handleInputChange(q.question_number, opt)}
                                                        />
                                                        <span className="opt-text">{opt}</span>
                                                    </label>
                                                ))}
                                        </div>
                                    ) : q.question_type === 'mcq_many' ? (
                                        <div className="options-grid">
                                            {q.options && JSON.parse(q.options)
                                                .filter(opt => opt.trim() !== '')
                                                .map((opt, index) => (
                                                    <label key={index} className="option-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={userAnswers[q.question_number]?.split(',').includes(opt)}
                                                            onChange={(e) => {
                                                                const currentAns = userAnswers[q.question_number] || "";
                                                                let newAns;
                                                                if (e.target.checked) {
                                                                    newAns = currentAns ? `${currentAns},${opt}` : opt;
                                                                } else {
                                                                    newAns = currentAns.split(',')
                                                                        .filter(item => item !== opt)
                                                                        .join(',');
                                                                }
                                                                handleInputChange(q.question_number, newAns);
                                                            }}
                                                        />
                                                        <span className="opt-text">{opt}</span>
                                                    </label>
                                                ))}
                                        </div>
                                    ) : (
                                        <div className="text-input-wrapper">
                                            <input
                                                type="text"
                                                className="q-input-inline"
                                                value={userAnswers[q.question_number] || ''}
                                                onChange={(e) => handleInputChange(q.question_number, e.target.value)}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="ielts-footer-nav">
                <div className="nav-grid">
                    {questions.map((q) => (
                        <div
                            key={q.question_number}
                            className={`nav-number ${userAnswers[q.question_number] ? 'answered' : ''}`}
                            onClick={() => scrollToQuestion(q.question_number)}
                        >
                            {q.question_number}
                        </div>
                    ))}
                </div>

                <button className="ielts-submit-btn" onClick={() => handleSubmit(false)}>
                    Finish Test
                </button>
            </div>
                    {resultData && (
                        <div className="score-popup-overlay">
                            <div className="score-popup">

                                <h2>
                                    {resultData.auto
                                        ? "Time's Up!"
                                        : "Test Completed"}
                                </h2>

                                <div className="big-score">
                                    {resultData.score}
                                </div>

                                <p className="score-label">
                                    IELTS Band Score
                                </p>

                                <p className="correct-text">
                                    Correct Answers:
                                    {' '}
                                    {resultData.correct}/{resultData.total}
                                </p>

                                <button
                                    className="back-home-btn"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    Back To Dashboard
                                </button>

                            </div>
                        </div>
                    )}
        </div>
    );
};

export default QuizPage;